import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Badge, Card, Col, Flex, Input, List, Row, Tooltip, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebounce } from 'use-debounce';
import { LoadingIndicator } from '../components/LoadingIndicator.tsx';
import { MainContent } from '../components/MainContent';
import type { OpenFrontDeskRequestsQuery } from '../graphql/generated/graphql.ts';
import { useCurrentContextStore } from '../hooks/store/useCurrentContextStore.ts';
import { SHORT_DATE_FORMAT } from '../utils/dateFormatUtils.ts';
import { patientFullName } from '../utils/patient.ts';
import { OPEN_FRONT_DESK_REQUESTS_QUERY } from './OpenFrontDeskRequests.gql';

export type Request = NonNullable<OpenFrontDeskRequestsQuery['openFrontDeskRequests']>[number];

export const OpenFrontDeskRequests: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const { setCurrentDoctorId } = useCurrentContextStore();

  const { data, loading } = useQuery(OPEN_FRONT_DESK_REQUESTS_QUERY);

  const filteredData: Map<string, Array<Request>> = useMemo(() => {
    if (!data) {
      return new Map();
    }

    const filtered = filterByTokens(data?.openFrontDeskRequests ?? [], debouncedSearch, [
      r => r.patientData.firstName,
      r => r.patientData.lastName,
      r => r.patientData.svnr,
      r => r.doctor.name,
      r => r.doctor.meAddress,
      r => r.doctor.assignedLabs.map(it => it.customerId),
      r => r.orders.map(order => order.number),
    ]);

    return filtered.reduce<Map<string, Array<Request>>>(
      (map, acc) => map.set(acc.doctor.name, [...(map.get(acc.doctor.name) ?? []), acc]),
      new Map()
    );
  }, [debouncedSearch, data]);

  if (loading) {
    return <LoadingIndicator height="200px" />;
  }

  return (
    <MainContent>
      <PageHeader
        title="Übersicht offener Anforderungen"
        style={{ padding: 0, paddingBottom: 'inherit' }}
        extra={
          <Flex wrap gap={token.paddingXS}>
            <Input
              autoFocus
              allowClear
              placeholder="Suche"
              value={search ?? ''}
              onChange={e => setSearch(e.target.value)}
              prefix={<SearchOutlined />}
              suffix={
                <Tooltip title="Suche nach Zuweiser Name, Zuweiser ME-Adresse, Zuweiser Kundennummer, Patient Vorname, Patient Nachname, Patient SVNR, oder Auftragsnummer">
                  <InfoCircleOutlined />
                </Tooltip>
              }
              className={css`
                width: 300px;
              `}
            />
          </Flex>
        }
      />
      <Row gutter={[16, 16]}>
        {Array.from(filteredData.entries()).map(entry => (
          <Col span={8} key={entry[0]}>
            <Card
              type="inner"
              size="small"
              hoverable
              title={
                <Markable tokens={debouncedSearch ?? ''}>
                  {entry[0]}{' '}
                  <Typography.Text type="secondary" className={monoFont}>
                    {entry[1][0].doctor.assignedLabs.map(it => it.customerId).join(', ')} {entry[1][0].doctor.meAddress}
                  </Typography.Text>
                </Markable>
              }
              extra={<Badge showZero count={entry[1].length} style={{ backgroundColor: token.colorPrimary }} />}
              className={css`
                height: 100%;
              `}
              onClick={() => {
                setCurrentDoctorId(entry[1][0].doctor.id);
                navigate('/offene-anforderungen');
              }}
            >
              <List
                size="small"
                className={css`
                  max-height: 130px;
                  overflow: auto;
                `}
                dataSource={entry[1]}
                renderItem={r => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Markable tokens={debouncedSearch ?? ''}>
                          {patientFullName(r.patientData)}
                          {r.patientData.svnr && (
                            <>
                              {' '}
                              <span className={monoFont}>({r.patientData.svnr})</span>
                            </>
                          )}
                        </Markable>
                      }
                      description={
                        <Markable tokens={debouncedSearch ?? ''}>
                          Auftrag <span className={monoFont}>{r.orders.map(it => it.number).join(', ')}</span> | Erstellt am{' '}
                          {dayjs(r.createdAt).format(SHORT_DATE_FORMAT)}
                          {r.sampledAt && <> | Entnahme am {dayjs(r.sampledAt).format(SHORT_DATE_FORMAT)}</>}
                        </Markable>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        ))}
      </Row>
    </MainContent>
  );
};
